import { useEffect, useState } from 'react'
import { getVerificationStatus, startVerificationPolling } from '@/api/domains'
import { VerificationStatusResponse } from '@/lib/types'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { RefreshCw, Clock, CheckCircle2, XCircle, Loader2, AlertCircle, Play } from 'lucide-react'
import { toast } from 'sonner'

interface VerificationStatusProps {
  accountId: string
  domain: string
  onStatusChange?: () => void
}

const statusConfig: Record<string, { icon: typeof CheckCircle2; color: string; label: string }> = {
  RUNNING: {
    icon: Loader2,
    color: 'bg-blue-500/15 text-blue-700 border-blue-300',
    label: 'Running',
  },
  SUCCEEDED: {
    icon: CheckCircle2,
    color: 'bg-teal-500/15 text-teal-700 border-teal-300',
    label: 'Completed',
  },
  FAILED: {
    icon: XCircle,
    color: 'bg-rose-500/15 text-rose-700 border-rose-300',
    label: 'Failed',
  },
  TIMED_OUT: {
    icon: Clock,
    color: 'bg-amber-500/15 text-amber-700 border-amber-300',
    label: 'Timed Out',
  },
  ABORTED: {
    icon: XCircle,
    color: 'bg-zinc-500/15 text-zinc-700 border-zinc-300',
    label: 'Aborted',
  },
  NOT_FOUND: {
    icon: AlertCircle,
    color: 'bg-zinc-500/15 text-zinc-700 border-zinc-300',
    label: 'No Active Verification',
  },
  ERROR: {
    icon: XCircle,
    color: 'bg-rose-500/15 text-rose-700 border-rose-300',
    label: 'Error',
  },
}

export function VerificationStatus({ accountId, domain, onStatusChange }: VerificationStatusProps) {
  const [status, setStatus] = useState<VerificationStatusResponse | null>(null)
  const [prevStatus, setPrevStatus] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [startingPoll, setStartingPoll] = useState(false)

  const handleStartPolling = async () => {
    setStartingPoll(true)
    const result = await startVerificationPolling(accountId, domain)
    setStartingPoll(false)

    if (result.success) {
      toast.success('Verification polling started')
      // Refresh status after a short delay to pick up the new execution
      setTimeout(() => fetchStatus(true), 2000)
    } else {
      toast.error(result.error || 'Failed to start verification polling')
    }
  }

  const fetchStatus = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }

    const result = await getVerificationStatus(accountId, domain)
    if (result.success && result.data) {
      setStatus(result.data)
    }

    setLoading(false)
    setRefreshing(false)
  }

  useEffect(() => {
    fetchStatus()
  }, [accountId, domain])

  // Detect status change and notify parent
  useEffect(() => {
    if (status?.status && prevStatus && status.status !== prevStatus) {
      onStatusChange?.()
    }
    if (status?.status) {
      setPrevStatus(status.status)
    }
  }, [status?.status])

  // Auto-refresh when status is RUNNING
  useEffect(() => {
    if (status?.status === 'RUNNING') {
      const interval = setInterval(() => {
        fetchStatus(true)
      }, 60000) // Refresh every 60 seconds
      return () => clearInterval(interval)
    }
  }, [status?.status])

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return '-'
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading Verification Status...
          </CardTitle>
        </CardHeader>
      </Card>
    )
  }

  if (!status) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Verification Polling Status</CardTitle>
          <CardDescription>
            Background process monitoring DNS verification
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Unable to fetch verification status. The Step Function may not be configured.
          </p>
        </CardContent>
      </Card>
    )
  }

  const config = statusConfig[status.status] || statusConfig.ERROR
  const StatusIcon = config.icon

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            Verification Polling Status
          </CardTitle>
          <CardDescription>
            Background process monitoring DNS verification
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleStartPolling}
            disabled={startingPoll || status?.status === 'RUNNING'}
          >
            <Play className={`mr-2 h-4 w-4 ${startingPoll ? 'animate-pulse' : ''}`} />
            {startingPoll ? 'Starting...' : 'Start Polling'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchStatus(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <Badge variant="outline" className={`${config.color} flex items-center gap-1.5 px-3 py-1`}>
            <StatusIcon className={`h-4 w-4 ${status.status === 'RUNNING' ? 'animate-spin' : ''}`} />
            {config.label}
          </Badge>
          {status.status === 'RUNNING' && (
            <span className="text-sm text-muted-foreground">
              Auto-refreshing every 60 seconds...
            </span>
          )}
        </div>

        {status.status !== 'NOT_FOUND' && (
          <dl className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
            <div>
              <dt className="text-muted-foreground">Started</dt>
              <dd className="font-medium">{formatDate(status.start_time)}</dd>
            </div>
            {status.stop_time && (
              <div>
                <dt className="text-muted-foreground">Completed</dt>
                <dd className="font-medium">{formatDate(status.stop_time)}</dd>
              </div>
            )}
          </dl>
        )}

        {status.error && (
          <div className="mt-4 rounded-md bg-destructive/15 p-3 text-sm text-destructive">
            {status.error}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
